import { responsiveFontSizes, createTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';
import chroma from 'chroma-js';
import { useAppSelector } from '../store';
import { useMemo } from 'react';
import { LoadingButtonProps } from '@mui/lab';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    dateSelector: true;
    text: true;
  }
}

declare module '@mui/material/Box' {
  interface BoxPropsVariantOverrides {
    stickyNavbar: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    gray: true;
    title: true;
    reminder: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    confirmed: true;
    tentative: true;
    inquiry: true;
    actual: true;
    'in-house': true;
    departed: true;
    cancelled: true;
    [any: string]: true;
  }
}

declare module '@mui/material/Avatar' {
  interface AvatarPropsVariantOverrides {
    confirmed: true;
    tentative: true;
    inquiry: true;
    actual: true;
    'in-house': true;
    departed: true;
    cancelled: true;
    [any: string]: true;
  }
}

declare module '@mui/material/styles' {
  interface Components {
    MuiLoadingButton?: {
      defaultProps?: Partial<LoadingButtonProps>;
      styleOverrides?: {
        root?: React.CSSProperties;
        [key: string]: any;
      };
      variants?: any[];
    };
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    pos: true;
  }
}

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,

    pos: 1024,
  },
};

export interface ColorSchemeInterface {
  error: string;
  waitlist: string;
  inHouse: string;
  departed: string;
  cancelled: string;
  inquiry: string;
  actual: string;
  paperBg: string;
  activeColumn: string;
  innerRow: string;
  hoverColor: string;
  newItem: string;
  bgColor: string;
  white: string;
  textColor: string;
  textGray: string;
  gray: string;
  lightGray: string;
  lightText: string;
  textSecondary: string;
  danger: string;
  warning: string;
  success: string;
  info: string;
  orange: string;
  darkGray: string;
  primary: string;
  secondary: string | undefined;
  highlight: string | undefined;
  borderColor: string;
  darkMode: boolean;
  backDrop: string;
  confirmed: string;
  dueOut: string;
  outOfOrder: string;
  vacantClean: string;
  tentative: string;
  outOfService: string;
  occupiedDirty: string;
  occupiedClean: string;
  occupied: string;
  vacantDirty: string;
  vacantInspected: string;
  offWhite: string;
  ligthGrayText: string;
  borderBottom: string;
  badge: string;
  badgeSelected: string;
  arrival: string;
  departures: string;
  reservations: string;
  imgGray: string;
  discText: string;
  iconBG: string;
  switchHvr: string;
  btnBG: string;
  borderHvr: string;
  iconSG: string;
  textCC: string;
  itemImgBG: string;
  foodBG: string;
  bevBG: string;
  foodText: string;
  bevText: string;
  scrollBar: string;
  scrollBarHvr: string;
  scrollBarBG: string;
  chipBG: string;
  chipAvatar: string;
  btnProceedBG: string;
  btnProceedHvr: string;
  cardBG: string;
  discountBG: string;
  lightSuccess: string;
  cashPaymentBG: string;
  roomChargesBG: string;
  otherTenders: string;
  black: string;
}

export const getComplementaryColor = (hexColor: string) =>
  chroma(hexColor)
    .set('hsl.h', (chroma(hexColor).hsl()[0] + 180) % 360)
    .hex();

export const generatePalette = (color: string) => {
  const numberOfColors = 16;
  return chroma
    .scale(['#FFF', chroma(color).luminance(0.5), '#000'])
    .mode('hsl')
    .colors(numberOfColors);
};

export const setOpacity = (color: string, opacity: number) =>
  chroma(color).alpha(opacity).hex();

export const getContrastText = (color: string) =>
  chroma(color).luminance() > 0.5 ? '#000000' : '#ffffff';

export const getColor = (): ColorSchemeInterface => {
  const darkMode = useAppSelector(
    (state) => state.configuration.settings.darkMode,
  );

  const { primary, secondary, highlight } = useAppSelector(
    (state) => state.configuration.settings.colorScheme,
  );

  const bgColor = darkMode ? '#181a1b' : '#F5F5F5';
  const white = darkMode ? '#010203' : '#FFFFFF';
  const textColor = darkMode ? '#FFFFFF' : '#010203';
  const textGray = darkMode ? '#FFFFFF' : '#9e9e9e';
  const gray = darkMode ? '#1e2021' : '#9e9e9e';
  const lightGray = '#F6F6F6';
  const lightText = darkMode ? '#FFFFFF99' : '#00000099';
  const borderColor = darkMode ? '#010203' : '#E0E0E0';
  const backDrop = darkMode ? '#000000cc' : '#ffffffcc';
  const textSecondary = '#637381';
  const danger = '#FF5630';
  const warning = '#FFAB00';
  const success = '#22C55E';
  const lightSuccess = '#F3F9F3';
  const error = '#FF5630';
  const info = '#00B8D9';
  const orange = '#FB9E3B';
  const darkGray = '#616161';
  const newItem = darkMode ? '#3a4d3b' : '#E8F5E9';
  const offWhite = darkMode ? '#010203' : '#FAFAFA';
  const paperBg = darkMode ? 'transparent' : '#FFFFFF';
  const borderBottom = darkMode ? 'transparent' : '#EEEEEE';
  const badge = '#DFE3E8';
  const badgeSelected = '#2B3493';

  // GRID
  const ligthGrayText = darkMode ? '#FFFFFF99' : '#212B3661';
  const hoverColor = darkMode ? '#181a1b' : '#F5F6FC';
  const innerRow = darkMode ? '#101112' : '#FAFAFA';
  const activeColumn = darkMode ? '#000623 ' : '#F5F6FB';

  // RESERVATION
  // const confirmed = '#00B8D9';
  // const dueOut = '#FF7043';
  // const outOfOrder = '#212B36';
  // const vacantClean = '#FFEE58';
  // const tentative = '#0D47A1';
  // const outOfService = '#919EAB';
  // const occupiedDirty = '#FF80AB';
  // const occupied = '#9575CD';
  // const vacantDirty = '#EF5350';
  // const vacantInspected = '#66BB6A';
  // const inHouse = '#7E57C2';

  // const departed = '#8b8d1183';
  // const cancelled = '#8d111183';
  // const inquiry = '#E8E8E9';
  // const actual = '#1d118d83';

  // RESERVATION
  const confirmed = '#00008B';
  const dueOut = '#A52A2A';
  const outOfOrder = '#000000';
  const vacantClean = '#FFFF00';
  const tentative = '#6fb3cb';
  const outOfService = '#808080';
  const occupiedDirty = '#FFA500';
  const occupiedClean = '#FFC0CB';
  const occupied = '#8A2BE2';
  const vacantDirty = '#FF0000';
  const vacantInspected = '#008000';
  const inHouse = '#7E57C2';

  const reservations = '#00B8D9';
  const departed = '#00000000';
  const departures = '#26A69A';
  const arrival = '#3D5AFE';
  const cancelled = '#FF5630';
  const inquiry = '#1DE9B6';
  const actual = '#CDDC39';
  const waitlist = '#D1D2E5';

  //POS
  const imgGray = '#EFF2F6';
  const discText = '#212B36';
  const iconBG = '#161C241F';
  const switchHvr = '#e0e0e0';
  const btnBG = '#2B34930A';
  const btnProceedBG = '#3f51b5';
  const btnProceedHvr = '#303e91';
  const borderHvr = '#2B349380';
  const iconSG = '#BDBDBD';
  const textCC = '#36454F';
  const itemImgBG = '#2B349314';
  const foodBG = '#22C55E0A';
  const bevBG = '#FF56300A';
  const foodText = '#22C55E';
  const bevText = '#FF5630';
  const scrollBar = '#C4CDD5';
  const scrollBarHvr = '#2B3493';
  const scrollBarBG = '#f1f1f1';
  const chipBG = '#FB9E3B';
  const chipAvatar = '#E65100';
  const cardBG = '#000000DE';
  const discountBG = '#F7F7FB';
  const cashPaymentBG = '#00BCD4';
  const roomChargesBG = '#448AFF';
  const otherTenders = '#AB47BC';
  const black = '#000000';

  return {
    occupiedClean,
    lightSuccess,
    lightGray,
    error,
    waitlist,
    reservations,
    departures,
    arrival,
    highlight,
    inHouse,
    departed,
    cancelled,
    inquiry,
    actual,
    paperBg,
    activeColumn,
    innerRow,
    hoverColor,
    newItem,
    bgColor,
    white,
    textColor,
    textGray,
    gray,
    lightText,
    textSecondary,
    danger,
    warning,
    success,
    info,
    orange,
    darkGray,
    primary,
    secondary,
    borderColor,
    darkMode,
    backDrop,
    confirmed,
    dueOut,
    outOfOrder,
    vacantClean,
    tentative,
    outOfService,
    occupiedDirty,
    occupied,
    vacantDirty,
    vacantInspected,
    offWhite,
    ligthGrayText,
    borderBottom,
    badge,
    badgeSelected,
    imgGray,
    discText,
    iconBG,
    switchHvr,
    btnBG,
    borderHvr,
    iconSG,
    textCC,
    itemImgBG,
    foodBG,
    bevBG,
    foodText,
    bevText,
    scrollBar,
    scrollBarHvr,
    scrollBarBG,
    chipBG,
    chipAvatar,
    btnProceedBG,
    btnProceedHvr,
    cardBG,
    discountBG,
    cashPaymentBG,
    roomChargesBG,
    otherTenders,
    black,
  };
};

const theme = () => {
  const colorScheme = useAppSelector(
    (state) => state.configuration.settings.colorScheme,
  );

  const darkMode = useAppSelector(
    (state) => state.configuration.settings.darkMode,
  );

  const { primary, success, danger, warning, orange, info, gray, textColor } =
    getColor();

  const secondary =
    colorScheme?.secondary || getComplementaryColor(colorScheme.primary);

  const primaryColorPalette = generatePalette(colorScheme.primary);
  const secondaryColorPalette = generatePalette(secondary);
  const errorColorPalette = generatePalette(danger);
  const successColorPalette = generatePalette(success);

  return useMemo(
    () =>
      responsiveFontSizes(
        createTheme({
          typography: {
            h1: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            h2: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            h3: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            h4: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            h5: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            h6: {
              fontFamily: ['Montserrat', 'sans-serif'].join(','),
            },
            fontFamily: ['Poppins', 'sans-serif'].join(','),
            fontSize: 12,
          },
          components: {
            MuiTypography: {
              variants: [
                {
                  props: { variant: 'gray' },
                  style: {
                    color: '#9e9e9e',
                  },
                },
                {
                  props: { variant: 'title' },
                  style: {
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    color: textColor,
                    paddingBottom: '16px',
                    letterSpacing: '0.15px',
                    fontSize: '20px',
                  },
                },
                {
                  props: { variant: 'reminder' },
                  style: {
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '20.02px',
                    letterSpacing: '.4px',
                    textAlign: 'center',
                  },
                },
              ],
            },
            MuiLoadingButton: {
              defaultProps: {
                disableElevation: true,
                disableRipple: true,
              },
              styleOverrides: {
                root: {
                  paddingTop: '5px',
                  paddingBottom: '5px',
                  boxShadow: 'none',
                  // minWidth: '100px',
                },
              },
            },
            MuiButton: {
              defaultProps: {
                disableElevation: true,
                disableRipple: true,
              },
              styleOverrides: {
                root: {
                  boxShadow: 'none',
                  // minWidth: '100px',
                },
              },
              variants: [
                {
                  props: { variant: 'contained' },
                  style: {
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    letterSpacing: '0.46px',
                    textTransform: 'none',
                    color: getContrastText(colorScheme.primary),
                  },
                },
                {
                  props: { variant: 'outlined' },
                  style: {
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    letterSpacing: '0.46px',
                    textTransform: 'none',
                    color: colorScheme.primary,
                  },
                },
                {
                  props: { variant: 'dateSelector' },
                  style: {
                    backgroundColor: darkMode
                      ? chroma(primaryColorPalette[14]).desaturate(0.75).hex()
                      : primaryColorPalette[1],
                    borderRadius: 10,
                    height: '50px',
                    '&:hover': {
                      backgroundColor: darkMode
                        ? chroma(primaryColorPalette[13]).desaturate(0.75).hex()
                        : primaryColorPalette[2],
                    },
                  },
                },

                {
                  props: { variant: 'text' },
                  style: {
                    color: colorScheme.primary || '#000000',
                    padding: '8px 22px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    letterSpacing: '0.46px',
                    textTransform: 'none',
                    ':active': {
                      color: gray,
                    },
                    ':hover': {
                      background: `${colorScheme.primary}12`,
                    },
                  },
                },
              ],
            },
            MuiCard: {
              styleOverrides: {
                root: {
                  backgroundColor: darkMode
                    ? chroma(primaryColorPalette[14]).desaturate(0.75).hex()
                    : primaryColorPalette[1],
                },
              },
            },
            MuiChip: {
              variants: [
                {
                  props: { variant: 'kanbanInProgress' },
                  style: {
                    backgroundColor: '#414cc8',
                    color: '#fff',
                  },
                },
                {
                  props: { variant: 'kanbanOnHold' },
                  style: {
                    backgroundColor: '#ffab00',
                    color: '#fff',
                  },
                },
                {
                  props: { variant: 'kanbanCompleted' },
                  style: {
                    backgroundColor: '#22c55e',
                    color: '#fff',
                  },
                },
                {
                  props: { variant: 'kanbanCancelled' },
                  style: {
                    backgroundColor: '#ff5630',
                    color: '#fff',
                  },
                },
                {
                  props: { variant: 'filled' },
                  style: {
                    backgroundColor: primaryColorPalette[darkMode ? 14 : 1],
                    color: textColor,
                  },
                },
                {
                  props: { variant: 'confirmed' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                    },
                    backgroundColor: success,
                    color: '#FFF',
                    ':hover': {
                      backgroundColor: '#118D57',
                    },
                  },
                },
                {
                  props: { variant: 'tentative' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                    },
                    backgroundColor: orange,
                    color: '#FFF',
                    ':hover': {
                      backgroundColor: '#C86904',
                    },
                  },
                },
                {
                  props: { variant: 'inquiry' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: '#E8E8E9',
                    ':hover': { backgroundColor: '#757575' },
                  },
                },
                {
                  props: { variant: 'actual' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: primary,
                    ':hover': { backgroundColor: '#1d118d' },
                  },
                },
                {
                  props: { variant: 'in-house' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: '#6a118d83',
                    ':hover': { backgroundColor: '#6a118d' },
                  },
                },
                {
                  props: { variant: 'departed' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: '#8b8d1183',
                    ':hover': { backgroundColor: '#8b8d11' },
                  },
                },
                {
                  props: { variant: 'cancelled' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: '#8d111183',
                    ':hover': { backgroundColor: '#8d1111' },
                  },
                },
                // ABK
                {
                  props: { variant: 'draft' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: '#3F4040',
                    ':hover': { backgroundColor: '#3f4040a4' },
                  },
                },
                {
                  props: { variant: 'posted' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: success,
                    ':hover': { backgroundColor: `${success}a4` },
                  },
                },
                {
                  props: { variant: 'submitted' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: primary,
                    ':hover': { backgroundColor: `${primary}a4` },
                  },
                },
                {
                  props: { variant: 'deleted' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: danger,
                    ':hover': { backgroundColor: `${danger}a4` },
                  },
                },
                {
                  props: { variant: 'disapproved' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: warning,
                    ':hover': { backgroundColor: `${warning}a4` },
                  },
                },
                {
                  props: { variant: 'cancelled' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: danger,
                    ':hover': { backgroundColor: `${danger}a4` },
                  },
                },
                {
                  props: { variant: 'canceled' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF!important',
                    },
                    backgroundColor: danger,
                    ':hover': { backgroundColor: `${danger}a4` },
                  },
                },
                {
                  props: { variant: 'active' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF',
                    },
                    backgroundColor: success,
                    ':hover': {
                      backgroundColor: `${success}a4`,
                    },
                  },
                },
                {
                  props: { variant: 'released' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      color: '#FFF',
                    },
                    backgroundColor: success,
                    ':hover': {
                      backgroundColor: `${success}a4`,
                    },
                  },
                },
                {
                  props: { variant: 'cleared' },
                  style: {
                    '.MuiChip-label': {
                      textTransform: 'capitalize',
                      // color: '#FFF',
                    },
                    // backgroundColor: success,
                    // ':hover': {
                    //   backgroundColor: `${success}a4`,
                    // },
                  },
                },
              ],
            },
            MuiAvatar: {
              variants: [
                {
                  props: { variant: 'kanbanInProgress' },
                  style: {
                    color: '#414cc8',
                    backgroundColor: '#FFF',
                  },
                },
                {
                  props: { variant: 'kanbanCompleted' },
                  style: {
                    color: '#22c55e',
                    backgroundColor: '#FFF',
                  },
                },
                {
                  props: { variant: 'kanbanCancelled' },
                  style: {
                    color: '#ff5630',
                    backgroundColor: '#FFF',
                  },
                },
                {
                  props: { variant: 'confirmed' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#118D57',
                    },
                  },
                },
                {
                  props: { variant: 'tentative' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#C86904',
                    },
                  },
                },
                {
                  props: { variant: 'inquiry' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#757575',
                    },
                  },
                },
                {
                  props: { variant: 'actual' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#1d118d',
                    },
                  },
                },
                {
                  props: { variant: 'in-house' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#6a118d',
                    },
                  },
                },
                {
                  props: { variant: 'departed' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF!important',
                      backgroundColor: '#8b8d11',
                    },
                  },
                },
                {
                  props: { variant: 'cancelled' },
                  style: {
                    '&.MuiChip-avatar': {
                      color: '#FFF',
                      backgroundColor: '#8d1111',
                    },
                  },
                },
              ],
            },
            MuiIconButton: {
              styleOverrides: {
                root: {
                  backgroundColor: primaryColorPalette[darkMode ? 15 : 0],
                  border: 0,
                  borderRadius: '50px',
                  // conflict with size props
                  // height: 30,
                  // width: 30,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                  },
                },
              },
            },
            MuiMenuItem: {
              defaultProps: {
                disableTouchRipple: true,
              },
            },
            MuiStepper: {
              styleOverrides: {
                root: {
                  '& .MuiStepLabel-iconContainer.Mui-disabled svg': {
                    // color: secondaryColorPalette[darkMode ? 11 : 4],
                    color: '#abaeb3',
                  },
                  '& .MuiStepConnector-line.MuiStepConnector-lineVertical': {
                    height: '100%',
                  },
                },
              },
            },
            MuiSwitch: {
              styleOverrides: {
                root: {
                  '& .Mui-checked': {
                    color: '#FFF!important',
                  },
                  '& .MuiSwitch-thumb': {
                    color: '#FFF!important',
                  },
                  '& .Mui-checked+.MuiSwitch-track': {
                    backgroundColor: `${success}!important`,
                  },
                },
              },
            },
            MuiTextField: {
              styleOverrides: {
                root: {
                  '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                },
              },
            },
            MuiAlert: {
              styleOverrides: {
                filledError: { backgroundColor: danger },
                filledInfo: { backgroundColor: info },
                filledSuccess: { backgroundColor: success },
                filledWarning: { backgroundColor: warning },
              },
            },
          },
          status: {
            danger: red[500],
          },
          palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: {
              100: primaryColorPalette[darkMode ? 14 : 1],
              200: primaryColorPalette[darkMode ? 13 : 2],
              300: primaryColorPalette[darkMode ? 12 : 3],
              400: primaryColorPalette[darkMode ? 11 : 4],
              500: primaryColorPalette[darkMode ? 10 : 5],
              600: primaryColorPalette[darkMode ? 9 : 6],
              700: primaryColorPalette[darkMode ? 8 : 7],
              800: primaryColorPalette[darkMode ? 7 : 8],
              900: primaryColorPalette[darkMode ? 6 : 9],
              main: colorScheme.primary,
              contrastText: getContrastText(colorScheme.primary),
            },
            secondary: {
              100: secondaryColorPalette[darkMode ? 14 : 1],
              200: secondaryColorPalette[darkMode ? 13 : 2],
              300: secondaryColorPalette[darkMode ? 12 : 3],
              400: secondaryColorPalette[darkMode ? 11 : 4],
              500: secondaryColorPalette[darkMode ? 10 : 5],
              600: secondaryColorPalette[darkMode ? 9 : 6],
              700: secondaryColorPalette[darkMode ? 8 : 7],
              800: secondaryColorPalette[darkMode ? 7 : 8],
              900: secondaryColorPalette[darkMode ? 6 : 9],
              main: secondary,
              contrastText: getContrastText(secondary),
            },
            info: {
              main: '#00B8D9',
            },
            error: {
              100: errorColorPalette[darkMode ? 14 : 1],
              200: errorColorPalette[darkMode ? 13 : 2],
              300: errorColorPalette[darkMode ? 12 : 3],
              400: errorColorPalette[darkMode ? 11 : 4],
              500: errorColorPalette[darkMode ? 10 : 5],
              600: errorColorPalette[darkMode ? 9 : 6],
              700: errorColorPalette[darkMode ? 8 : 7],
              800: errorColorPalette[darkMode ? 7 : 8],
              900: errorColorPalette[darkMode ? 6 : 9],
              main: danger,
            },
            warning: { main: warning },
            success: {
              100: successColorPalette[darkMode ? 14 : 1],
              200: successColorPalette[darkMode ? 13 : 2],
              300: successColorPalette[darkMode ? 12 : 3],
              400: successColorPalette[darkMode ? 11 : 4],
              500: successColorPalette[darkMode ? 10 : 5],
              600: successColorPalette[darkMode ? 9 : 6],
              700: successColorPalette[darkMode ? 8 : 7],
              800: successColorPalette[darkMode ? 7 : 8],
              900: successColorPalette[darkMode ? 6 : 9],
              main: success,
            },
            background: {
              default: primaryColorPalette[darkMode ? 15 : 0],
            },
          },
          breakpoints,
        }),
      ),
    [darkMode],
  );
};

export default theme;
